import { useState, useEffect, useCallback, useRef } from "react";
import { supabase, dbApplyWo, dbLoadState, dbLoadStateLite, dbSavePlayers, dbInsertResult, dbDeleteChallenge, dbInsertNotifications, dbInsertPositionHistory, dbUploadPhoto, dbSubscribe, dbUnsubscribe } from "../lib/supabase";
import { ADMIN_PASS, GENDER } from "../constants";
import { isExpired } from "../lib/helpers";
import { nowBR } from "../lib/timezone";

export { nowBR };

// ─── AUTO-EXPIRAÇÃO DE DESAFIOS ───────────────────────────────────────────────

async function fetchChallengeStatus(id, gender) {
  const table = GENDER.tables[gender]?.challenges ?? GENDER.tables.M.challenges;
  const { data, error } = await supabase
    .from(table)
    .select("status, confirmed_date")
    .eq("id", id)
    .single();
  if (error || !data) return null;
  return { status: data.status, confirmedDate: data.confirmed_date };
}

async function autoExpireChallenges(loaded, gender) {
  const expired = loaded.challenges.filter(
    (c) => c.status === "aguardando_confirmacao" && !c.confirmedDate && isExpired(c)
  );
  if (expired.length === 0) return loaded;

  const s = JSON.parse(JSON.stringify(loaded));
  const now = nowBR();
  let appliedAny = false;

  for (const ch of expired) {
    try {
      const dbState = await fetchChallengeStatus(ch.id, gender);
      if (!dbState) {
        s.challenges = s.challenges.filter((c) => c.id !== ch.id);
        continue;
      }
      if (dbState.status !== "aguardando_confirmacao" || dbState.confirmedDate) {
        console.log(`autoExpire: desafio ${ch.id} ignorado (status no DB: ${dbState.status})`);
        continue;
      }
    } catch (e) {
      console.warn(`autoExpire: erro ao verificar desafio ${ch.id} — pulando`, e);
      continue;
    }

    const byChallenger = (ch.lastProposedBy || "challenger") === "challenger";

    if (byChallenger) {
      // A penalidade é aplicada no banco, em uma única transação (apply_wo_penalty):
      // trava o desafio, recalcula posições com dados atuais e grava tudo ou nada.
      // Se outra sessão já processou este desafio, a função devolve applied=false.
      try {
        const r = await dbApplyWo({ gender, challengeId: ch.id, type: "wo-challenged", origin: "expiracao" });
        if (r && r.applied) appliedAny = true;
        else console.log(`autoExpire: desafio ${ch.id} não aplicado (${r && r.reason})`);
      } catch (e) {
        console.warn(`autoExpire: erro ao aplicar W.O. do desafio ${ch.id} — será tentado de novo`, e);
      }
    } else {
      const chd = s.players.find((p) => p.id === ch.challengedId);
      const chr = s.players.find((p) => p.id === ch.challengerId);

      s.challenges = s.challenges.filter((c) => c.id !== ch.id);

      const notifs = [
        ...(chr
          ? [{
              playerId: chr.id,
              text: `Desafio cancelado automaticamente: você não confirmou as novas datas propostas por ${chd?.name || "seu adversário"} no prazo.`,
              date: now,
              read: false,
            }]
          : []),
        ...(chd
          ? [{
              playerId: chd.id,
              text: `Desafio cancelado automaticamente: ${chr?.name || "seu adversário"} não confirmou suas datas propostas no prazo. Nenhuma penalidade foi aplicada.`,
              date: now,
              read: false,
            }]
          : []),
      ];
      notifs.forEach((n) => s.notifications.unshift({ ...n, id: Date.now() + Math.random() }));

      try {
        await dbDeleteChallenge(ch.id, gender);
        if (notifs.length) await dbInsertNotifications(notifs, gender);
      } catch (e) {
        console.warn("autoExpire cancel error:", e);
      }
    }
  }

  // Houve W.O. aplicado no banco: devolve o estado fresco (posições, resultados, etc.)
  if (appliedAny) {
    try {
      return await dbLoadStateLite(gender, s);
    } catch (e) {
      console.warn("autoExpire: erro ao recarregar estado após W.O.", e);
    }
  }
  return s;
}

// ─── HOOK ─────────────────────────────────────────────────────────────────────

export function useAuth() {
  const [state, setState]             = useState(null);
  const [screen, setScreen]           = useState("login");
  const [currentUser, setCurrentUser] = useState(null);

  const [gender, setGenderRaw] = useState(() => {
    return localStorage.getItem("cdc_gender") || GENDER.M;
  });

  const [tab, setTab]                       = useState("ranking");
  const [msg, setMsg]                       = useState("");
  const [msgType, setMsgType]               = useState("success");
  const [hoveredCell, setHoveredCell]       = useState(null);
  const [hoveredNav, setHoveredNav]         = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [loginUser, setLoginUser]   = useState("");
  const [loginPass, setLoginPass]   = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginSugg, setLoginSugg]   = useState([]);

  const [passModal, setPassModal] = useState(false);
  const [oldPass, setOldPass]     = useState("");
  const [newPass1, setNewPass1]   = useState("");
  const [newPass2, setNewPass2]   = useState("");

  const [profileModal, setProfileModal] = useState(false);
  const [profileEmail, setProfileEmail] = useState("");
  const [profilePhone, setProfilePhone] = useState("");

  const [photoUploading, setPhotoUploading] = useState(false);

  // Ref para os channels Realtime ativos
  const rtChannels = useRef([]);
  // Ref para o estado atual — usado dentro do onRefresh sem criar dependência de closure
  const stateRef = useRef(null);
  useEffect(() => { stateRef.current = state; }, [state]);
  // Ref de boot guard — bloqueia o onRefresh do Realtime enquanto o autoExpireChallenges
  // ainda está gravando no banco, evitando que eventos gerados pelo próprio boot
  // ressuscitem desafios já expirados no estado React.
  const bootingRef = useRef(false);
  const onVisibleRef = useRef(null);

  // ── Boot ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&family=Barlow:wght@300;400;500;600;700&display=swap";
    document.head.appendChild(link);

    const favicon = document.querySelector("link[rel~='icon']") || document.createElement("link");
    favicon.rel = "icon";
    favicon.type = "image/svg+xml";
    favicon.href = "data:image/svg+xml," + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><text y="82" font-size="85">🎾</text></svg>');
    document.head.appendChild(favicon);

    let meta = document.querySelector("meta[name=viewport]");
    if (!meta) { meta = document.createElement("meta"); meta.name = "viewport"; document.head.appendChild(meta); }
    meta.content = "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no";

    const savedGender = localStorage.getItem("cdc_gender") || GENDER.M;

    (async () => {
      bootingRef.current = true;
      try {
        const loaded = await dbLoadState(savedGender);
        const processed = await autoExpireChallenges(loaded, savedGender);
        setState(processed);
        stateRef.current = processed;

        const savedUser = localStorage.getItem("cdc_user");
        if (savedUser) {
          if (savedUser === "admin") {
            setCurrentUser("admin"); setScreen("admin"); setTab("agenda");
          } else {
            const player = processed.players.find(p => p.id === parseInt(savedUser));
            if (player) { setCurrentUser(player.id); setScreen("player"); setTab("minha"); }
          }
        } else {
          setScreen("public");
        }
      } catch (e) {
        console.error("Erro ao carregar dados do Supabase:", e);
        setState({ players: [], challenges: [], results: [], notifications: [], blockedDates: [], nextChallengeId: 1 });
      } finally {
        // Aguarda 3s após o boot para garantir que todos os eventos Realtime
        // gerados pelo autoExpireChallenges (saves, deletes, inserts) já foram
        // absorvidos pelo debounce do dbSubscribe (1.5s) antes de liberar o onRefresh.
        setTimeout(() => { bootingRef.current = false; }, 3000);
      }

      // ── Realtime: usa dbLoadStateLite para re-fetches leves ────────────────
      // Não rebusca fotos (já em cache) nem dados estáticos — ~95% menos egress
      const onRefresh = async () => {
        // Não rebusca se a aba está em background — aguarda o próximo evento
        if (document.visibilityState === "hidden") return;
        // Não rebusca durante o boot — evita que eventos do autoExpireChallenges
        // ressuscitem desafios já expirados sobrescrevendo o estado processado.
        if (bootingRef.current) return;
        try {
          const g = localStorage.getItem("cdc_gender") || GENDER.M;
          const lite = await dbLoadStateLite(g, stateRef.current);
          setState(lite);
        } catch (e) {
          console.warn("Realtime refresh error:", e);
        }
      };

      rtChannels.current = dbSubscribe(savedGender, onRefresh);

      // Re-fetch ao voltar para a aba — cobre eventos perdidos enquanto estava em background
      const onVisible = () => { if (document.visibilityState === "visible") onRefresh(); };
      document.addEventListener("visibilitychange", onVisible);
      onVisibleRef.current = onVisible;
    })();

    return () => {
      dbUnsubscribe(rtChannels.current);
      if (onVisibleRef.current) document.removeEventListener("visibilitychange", onVisibleRef.current);
    };
  }, []);

  const save = useCallback((s) => setState(s), []);

  // ── Troca de gênero ─────────────────────────────────────────────────────────
  async function setGender(g) {
    if (g === gender) return;
    setGenderRaw(g);
    localStorage.setItem("cdc_gender", g);

    if (currentUser && currentUser !== "admin") {
      setCurrentUser(null);
      setScreen("public");
      localStorage.removeItem("cdc_user");
    }

    await dbUnsubscribe(rtChannels.current);

    setState(null);
    try {
      const loaded = await dbLoadState(g);
      const processed = await autoExpireChallenges(loaded, g);
      setState(processed);
      stateRef.current = processed;
    } catch (e) {
      console.error("Erro ao carregar dados:", e);
      setState({ players: [], challenges: [], results: [], notifications: [], blockedDates: [], nextChallengeId: 1 });
    }

    const onRefresh = async () => {
      if (document.visibilityState === "hidden") return;
      try {
        const lite = await dbLoadStateLite(g, stateRef.current);
        setState(lite);
      } catch (e) {
        console.warn("Realtime refresh error:", e);
      }
    };
    if (onVisibleRef.current) document.removeEventListener("visibilitychange", onVisibleRef.current);
    const onVisible = () => { if (document.visibilityState === "visible") onRefresh(); };
    document.addEventListener("visibilitychange", onVisible);
    onVisibleRef.current = onVisible;
    rtChannels.current = dbSubscribe(g, onRefresh);
  }

  // ── Utilities ───────────────────────────────────────────────────────────────
  function flash(m, type = "success") {
    setMsg(m); setMsgType(type);
    setTimeout(() => setMsg(""), 3500);
  }

  async function withDB(fn, successMsg) {
    try { await fn(); if (successMsg) flash(successMsg); }
    catch (e) { console.error("DB error:", e); flash("Erro ao salvar. Verifique a conexão.", "error"); }
  }

  // ── Login / logout ──────────────────────────────────────────────────────────
  function handleLoginInput(val) {
    setLoginUser(val);
    if (!state || val.length < 2) { setLoginSugg([]); return; }
    const q = val.toLowerCase();
    setLoginSugg(state.players.filter(p => p.name.toLowerCase().includes(q)).slice(0, 5));
  }

  function login() {
    if (!state) return;
    if (loginUser.trim().toLowerCase() === "admin" && loginPass === ADMIN_PASS) {
      setCurrentUser("admin"); setScreen("admin"); setTab("agenda");
      setLoginError(""); setLoginSugg([]); localStorage.setItem("cdc_user", "admin"); return;
    }
    const p = state.players.find(x => x.name.toLowerCase() === loginUser.trim().toLowerCase());
    if (p && loginPass === p.password) {
      setCurrentUser(p.id); setScreen("player"); setTab("minha");
      setLoginError(""); setLoginSugg([]); localStorage.setItem("cdc_user", String(p.id)); return;
    }
    setLoginError("Usuário ou senha incorretos.");
  }

  function logout() {
    setCurrentUser(null); setScreen("login");
    setLoginUser(""); setLoginPass(""); setLoginSugg([]);
    localStorage.removeItem("cdc_user");
    // Estado já está em memória — não precisa rebuscar no logout
  }

  // ── Troca de senha ──────────────────────────────────────────────────────────
  async function changePassword() {
    const me = currentUser !== "admin" ? state?.players.find(p => p.id === currentUser) : null;
    if (!me) return;
    if (!oldPass || !newPass1 || !newPass2) { flash("Preencha todos os campos.", "error"); return; }
    if (oldPass !== me.password) { flash("Senha atual incorreta.", "error"); return; }
    if (newPass1 !== newPass2) { flash("As novas senhas não coincidem.", "error"); return; }
    if (newPass1.length < 4) { flash("A nova senha deve ter ao menos 4 caracteres.", "error"); return; }
    const s = JSON.parse(JSON.stringify(state));
    s.players.find(x => x.id === me.id).password = newPass1;
    save(s);
    try { await dbSavePlayers([s.players.find(x => x.id === me.id)], gender); }
    catch (e) { flash("Erro ao salvar senha.", "error"); return; }
    setOldPass(""); setNewPass1(""); setNewPass2(""); setPassModal(false);
    flash("Senha alterada com sucesso!");
  }

  // ── Upload de foto — agora via Supabase Storage ────────────────────────────
  // Fluxo: comprime no canvas → dbUploadPhoto (Storage) → salva URL no banco
  // A foto NÃO é mais salva como base64 na coluna photo — apenas a URL pública.
  function uploadPhoto(e) {
    const file = e.target.files[0];
    if (!file || !currentUser || currentUser === "admin") return;
    if (file.size > 5 * 1024 * 1024) { flash("Imagem muito grande. Máximo 5MB.", "error"); return; }

    setPhotoUploading(true);
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = async () => {
      URL.revokeObjectURL(objectUrl);

      // Comprime para max 200×200px, JPEG 70%
      const MAX = 200;
      let { width, height } = img;
      if (width > height) { height = Math.round((height * MAX) / width); width = MAX; }
      else { width = Math.round((width * MAX) / height); height = MAX; }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      const compressed = canvas.toDataURL("image/jpeg", 0.7);

      if (compressed.length > 80 * 1024) {
        flash("Não foi possível comprimir a imagem. Tente outra foto.", "error");
        setPhotoUploading(false); return;
      }

      try {
        // Determina o nome da tabela para nomear o arquivo no Storage
        const tableName = GENDER.tables[gender]?.players ?? GENDER.tables.M.players;

        // Upload para o Storage — retorna URL pública
        const photoUrl = await dbUploadPhoto(currentUser, compressed, tableName);

        // Atualiza estado local e banco
        const s = JSON.parse(JSON.stringify(state));
        const p = s.players.find(x => x.id === currentUser);
        if (p) {
          p.photo = photoUrl;
          save(s);
          stateRef.current = s;
          await dbSavePlayers([p], gender);
          flash("Foto atualizada!");
        }
      } catch (err) {
        console.error("Erro no upload da foto:", err);
        flash("Erro ao enviar foto. Tente novamente.", "error");
      }

      setPhotoUploading(false);
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      flash("Erro ao processar imagem.", "error");
      setPhotoUploading(false);
    };

    img.src = objectUrl;
  }

  // ── Salvar perfil ───────────────────────────────────────────────────────────
  async function saveProfile() {
    const me = currentUser !== "admin" ? state?.players.find(p => p.id === currentUser) : null;
    if (!me) return;
    const s = JSON.parse(JSON.stringify(state));
    const p = s.players.find(x => x.id === me.id);
    if (!p) return;
    p.email = profileEmail; p.phone = profilePhone;
    try {
      await dbSavePlayers([p], gender);
    } catch (e) { flash("Erro ao salvar perfil.", "error"); return; }
    save(s); setProfileModal(false); flash("Perfil atualizado!");
  }

  return {
    state, save,
    screen, setScreen,
    currentUser, setCurrentUser,
    gender, setGender,
    tab, setTab,
    msg, msgType,
    hoveredCell, setHoveredCell,
    hoveredNav, setHoveredNav,
    mobileMenuOpen, setMobileMenuOpen,
    loginUser, setLoginUser,
    loginPass, setLoginPass,
    loginError,
    loginSugg, setLoginSugg,
    passModal, setPassModal,
    oldPass, setOldPass,
    newPass1, setNewPass1,
    newPass2, setNewPass2,
    profileModal, setProfileModal,
    profileEmail, setProfileEmail,
    profilePhone, setProfilePhone,
    photoUploading,
    flash, withDB,
    handleLoginInput, login, logout,
    changePassword, uploadPhoto, saveProfile,
  };
}
